import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  isAppViewport,
  isMobileViewport,
  viewportMatches,
} from '@system-dyg/shared/utils';
import {
  UiMenubarItem,
  UiMenubarComponent,
} from '../../organisms/menubar/menubar.component';
import { UiNotificationMenuItem } from '../../molecules/notification-menu/notification-menu.component';
import {
  UiAppHeaderComponent,
  UiProfileMenuItem,
} from '../../organisms/app-header/app-header.component';
import {
  UiAppSidebarComponent,
  UiAppSidebarItem,
} from '../../organisms/app-sidebar/app-sidebar.component';
import { UiBottomNavigationComponent } from '../../organisms/bottom-navigation/bottom-navigation.component';
import { UiBottomNavItem } from '../../organisms/bottom-navigation/bottom-navigation.types';
import { UiBreadcrumbItem } from '../../atoms/breadcrumb/breadcrumb.component';
import {
  UiTitleBarComponent,
  UiTitleBarMode,
} from '../../organisms/title-bar/title-bar.component';

export type { UiAppSidebarItem, UiBottomNavItem };

/**
 * Template: shell de página base para todas las vistas de la aplicación.
 *
 * Compone tres organismos fijos:
 *  - ui-app-header   (posición fixed, z-index 100)
 *  - ui-menubar      (posición fixed, z-index 99, bajo el header)
 *  - ui-title-bar    (dentro del área scrollable)
 *
 * Slot opcional `slot-below-title-bar` (p. ej. `ui-icon-tab-bar`) entre el title-bar
 * y el contenido. El layout no importa el átomo: la vista proyecta lo que necesite.
 *
 * Y proyecta el contenido principal (ui-data-table, formulario, etc.) vía `ng-content`.
 *
 * No contiene lógica de negocio ni depende de servicios de dominio.
 * Las alturas del header y menubar se controlan con las custom properties
 * CSS `--ui-layout-header-h` y `--ui-layout-menubar-h` (sobreescribibles desde la app).
 *
 * @example
 * ```html
 * <ui-app-layout mainLabel="Gestión de propiedades">
 *   <div data-ui-app-header-nav>...</div>
 *   <div data-ui-app-header-end-start>...</div>
 *   <div data-ui-menubar-end>...</div>
 *   <ui-data-table [columns]="cols" [rows]="rows"></ui-data-table>
 * </ui-app-layout>
 * ```
 */
export type UiAppLayoutVariant =
  | 'general'
  | 'control-panel'
  | 'report'
  | 'form'
  | 'welcome'
  | 'sidebar'
  | 'portal';

@Component({
  selector: 'ui-app-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiAppHeaderComponent,
    UiAppSidebarComponent,
    UiBottomNavigationComponent,
    UiMenubarComponent,
    UiTitleBarComponent,
  ],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  host: {
    class: 'ui-app-layout-host',
    '[class.ui-app-layout-host--animated]': 'animated()',
    '[class.ui-app-layout-host--compact]': 'isCompactChrome()',
  },
})
export class UiAppLayoutComponent {
  /**
   * Variante del layout.
   * - `'sidebar'`  → App Header + Sidebar + Menubar + Title Bar + contenido.
   * - `'portal'`   → Sidebar a altura completa con el logo integrado.
   * - `'welcome'`  → Solo App Header + contenido (panel de bienvenida post-login).
   * @default 'sidebar'
   */
  readonly variant = input<UiAppLayoutVariant>('sidebar');

  /**
   * Etiqueta accesible para el elemento `<main>`.
   * Describe el propósito del área de contenido a lectores de pantalla.
   * @default 'Contenido principal'
   */
  readonly mainLabel = input('Contenido principal');

  // ── App Header ────────────────────────────────────────────────────────────
  readonly logoSrc = input<string | null>(null);
  readonly logoAlt = input('');
  readonly logoHref = input<string | null>(null);
  readonly navTitle = input<string | null>(null);
  readonly mobileNavTitle = input<string | null>(null);
  readonly userAvatarLabel = input<string | null>(null);

  // ── Menubar ──────────────────────────────────────────────────────────────
  readonly showMenubar = input(true);
  readonly menubarItems = input<readonly UiMenubarItem[]>([]);
  readonly menubarAriaLabel = input('Menú principal');
  readonly menubarHorizontalScroll = input(false);

  // ── Title Bar (solo variant != 'welcome') ────────────────────────────────
  readonly showTitleBar = input(true);
  readonly titleBarMode = input<UiTitleBarMode>('data-table');
  /** En modo report, activa la segunda fila de filtros del title bar. */
  readonly showSecondFilterRow = input(false);
  /** Activa el botón para desplegar filas de filtros adicionales. */
  readonly showAdditionalFilterRows = input(false);
  readonly title = input<string>('');
  readonly breadcrumbItems = input<readonly UiBreadcrumbItem[]>([]);
  readonly breadcrumbHome = input<UiBreadcrumbItem | null>(null);
  readonly breadcrumbAriaLabel = input('Ruta de navegación');

  // ── App Header — perfil de usuario ───────────────────────────────────────
  readonly profileUserName = input('');
  readonly profileUserRole = input('');
  readonly profileUserEmail = input('');
  /** Si no se indica, el header usa los defaults del menú según su variante. */
  readonly profileMenuItems = input<readonly UiProfileMenuItem[] | undefined>(
    undefined,
  );

  // ── App Header — botón Inicio ─────────────────────────────────────────────
  readonly showHome = input(true);

  /**
   * Muestra el slot de filtro de empresas en el header
   * (`[data-ui-app-header-end-start]`).
   * @default false
   */
  readonly showCompanyFilter = input(false);
  readonly notificationCount = input<number | null>(null);
  readonly messageCount = input<number | null>(null);
  readonly notificationItems = input<readonly UiNotificationMenuItem[]>([]);
  /** `null` conserva el default del header: visible solo en portal. */
  readonly showMessages = input<boolean | null>(null);

  // ── Sidebar (solo variant 'sidebar') ───────────────────────────────────────
  readonly sidebarItems = input<readonly UiAppSidebarItem[]>([]);
  readonly sidebarSelectedValue = input<string | null>(null);
  readonly sidebarAriaLabel = input('Navegación de módulos');

  // ── Barra inferior (solo variant 'portal' y cromado compacto) ─────────────
  /** Sin ítems no se renderiza: los consumidores actuales no cambian. */
  readonly bottomNavItems = input<readonly UiBottomNavItem[]>([]);
  readonly bottomNavAriaLabel = input('Navegación principal');

  // ── App Header — outputs ──────────────────────────────────────────────────
  readonly homeClick = output<void>();
  readonly logoutClick = output<void>();
  readonly notificationItemSelect = output<UiNotificationMenuItem>();
  readonly notificationViewAll = output<void>();
  readonly profileMenuItemClick = output<{ id: string }>();

  // ── Sidebar — outputs ──────────────────────────────────────────────────────
  readonly sidebarItemSelect = output<string>();

  // ── Sidebar collapsed (estado controlado por la app consumidora) ─────────
  readonly sidebarCollapsed = input(false);

  // ── Sidebar — toggle output ──────────────────────────────────────────────
  readonly sidebarToggle = output<void>();

  private readonly contentRef =
    viewChild<ElementRef<HTMLElement>>('contentRef');

  /**
   * Transiciones del chrome lateral (sidebar / main / menubar). Se activan
   * solo tras `enableSidebarTransitions()` desde el App Shell, después del
   * primer NavigationEnd + asentamiento. Evita el slide en F5 de rutas profundas.
   */
  protected readonly animated = signal(false);

  /**
   * Cromado compacto: solo variante `portal`, en móvil vertical (`md`) o móvil
   * en landscape corto. Las tabletas conservan el escritorio, por eso no usa
   * `isAppViewport()`. Es la única fuente del umbral: el SCSS reacciona a la
   * clase `--compact`, no a media queries propias.
   */
  private readonly isNarrowViewport = isMobileViewport();
  private readonly isShortViewport = viewportMatches(
    '(max-height: 540px) and (orientation: landscape)',
  );
  protected readonly isCompactChrome = computed(
    () =>
      this.variant() === 'portal' &&
      (this.isNarrowViewport() || this.isShortViewport()),
  );

  /**
   * Tableta en variante `portal`: sidebar fijo siempre colapsado y sin botón
   * de menú. No altera `sidebarCollapsed`; en escritorio vuelve su estado.
   */
  private readonly isAppSizedViewport = isAppViewport();
  protected readonly isTabletChrome = computed(
    () =>
      this.variant() === 'portal' &&
      this.isAppSizedViewport() &&
      !this.isCompactChrome(),
  );

  protected readonly effectiveSidebarCollapsed = computed(
    () => this.isTabletChrome() || this.sidebarCollapsed(),
  );

  /** Apertura del sidebar como panel superpuesto en cromado compacto. */
  protected readonly drawerOpen = signal(false);

  protected readonly showBottomNav = computed(
    () => this.isCompactChrome() && this.bottomNavItems().length > 0,
  );

  constructor() {
    effect(() => {
      if (this.isCompactChrome()) return;
      this.drawerOpen.set(false);
    });
  }

  /** Activa las transiciones de colapso del sidebar (llamar desde el shell). */
  enableSidebarTransitions(): void {
    this.animated.set(true);
  }

  protected onMenuToggle(): void {
    if (this.isCompactChrome()) {
      this.drawerOpen.update((open) => !open);
      return;
    }
    this.sidebarToggle.emit();
  }

  protected onSidebarItemSelect(value: string): void {
    this.drawerOpen.set(false);
    this.sidebarItemSelect.emit(value);
  }

  protected closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closeDrawer();
  }

  /**
   * Lleva el contenido al inicio. El layout no escucha rutas: el App Shell lo
   * invoca al terminar cada navegación, porque con una instancia estable el
   * contenedor de scroll ya no se recrea entre vistas.
   */
  scrollContentToTop(): void {
    this.contentRef()?.nativeElement.scrollTo({ top: 0 });
  }
}
